import React, { useState } from "react";
import { Formik } from "formik";
import { Button, Form, InlineLoading, TextInput } from "carbon-components-react";
import { Save20, TrashCan20 } from "@carbon/icons-react";
import { ReserveRequirement } from "./index";
import { validationSchema } from "./requirements-form-values";
import { EditingState } from "web_modules/shared/BaseTable";
import { WS_NAME } from "underwriter_dashboard/shared/WorksheetEditor/wsDefs";
import { AppShape } from "@eave/shared/types/api";
import APIClient from "web_modules/APIClient";
import {
  attachChildToParentWS,
  deleteWSValue,
  updateWSValue,
} from "underwriter_dashboard/shared/WorksheetEditor/wsHelpers";

interface Props {
  app: AppShape;
  loadApp: Function;
  dispatch?: Function;
  fetchReserves: Function;
  editingMode: EditingState; // The editingMode for the reserve requirements, not for the parent-level reserve
  setFormState: any;
  setFormUpdates: any;
  reserveId: string;
  reserveVersion: string;
  reserveRequirement: ReserveRequirement;
}

const ReserveRequirements: React.FC<Props> = ({
  app,
  loadApp,
  fetchReserves,
  dispatch,
  editingMode,
  reserveId,
  reserveVersion,
  reserveRequirement,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);

  const createReservesRequirementsWorksheet = async (reservesId, values: ReserveRequirement) => {
    const { id: appId } = app;

    // 1. create a new child "reserve requirement" ws
    const creationPayload = {
      application_id: appId,
      worksheet_name: WS_NAME.RESERVE_REQUIREMENT,
    };
    const response = await APIClient.post(`/worksheets`, creationPayload);
    const { worksheet: childWorksheet } = response as any;
    const { id: reservesRequirementsId, version } = childWorksheet;

    // 2. attach to parent "reserves" ws
    return attachChildToParentWS(
      appId,
      reservesRequirementsId,
      reservesId,
      "reserve_requirements",
      reserveVersion,
    ).then(() => {
      updateWSValue(appId, values, reservesRequirementsId, version);
    });
  };

  const updateReservesRequirementsWorksheet = async values => {
    const { id: appId } = app;
    const { id: worksheetId, version: worksheetVersion } = values;

    const payload = { ...values };
    delete payload.array_index; // necessary for deletion, but an invalid field for updates

    updateWSValue(appId, payload, worksheetId, worksheetVersion);
  };

  const handleDeleteReserveRequirement = async (reason, number_of_months, array_index) => {
    setLoading(true);
    if (confirm(`Delete "${reason}" with value of "${number_of_months}"?`)) {
      deleteWSValue(reserveId, "reserve_requirements", reserveVersion, array_index);
      await fetchReserves();
      loadApp(dispatch, app.id);
    }
  };

  const onSubmit = async (values: ReserveRequirement) => {
    setLoading(true);

    if (editingMode === EditingState.CREATE) {
      await createReservesRequirementsWorksheet(reserveId, values);
      await fetchReserves();
      setLoading(false);
      loadApp(dispatch, app.id);
    }
    if (editingMode === EditingState.UPDATE) {
      await updateReservesRequirementsWorksheet(values);
      await fetchReserves();
      setLoading(false);
      loadApp(dispatch, app.id);
    }
  };

  return (
    <>
      <Formik
        enableReinitialize
        onSubmit={onSubmit}
        initialValues={reserveRequirement}
        validationSchema={validationSchema}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => {
          const { reason, number_of_months } = values;

          return (
            <Form
              onSubmit={e => {
                e.preventDefault();
                handleSubmit();
              }}
              style={{ display: "flex", width: "100%", justifyContent: "space-around" }}
            >
              <div style={{ width: "250px" }}>
                <TextInput
                  id="reason"
                  name="reason"
                  size="sm"
                  value={reason}
                  labelText="Reason"
                  placeholder="Base, payment shock, etc"
                  type="text"
                  invalid={!!errors.reason && touched.reason}
                  invalidText={errors.reason}
                  onChange={e => {
                    !dirty && setDirty(true);
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                />
              </div>
              <div style={{ width: "120px" }}>
                <TextInput
                  id="number_of_months"
                  name="number_of_months"
                  size="sm"
                  value={number_of_months}
                  labelText="Number of months"
                  placeholder="6"
                  type="text"
                  invalid={!!errors.number_of_months && touched.number_of_months}
                  invalidText={errors.number_of_months}
                  onChange={e => {
                    !dirty && setDirty(true);
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  style={{ maxWidth: "100px" }}
                />
              </div>
              <div style={{ display: "flex" }}>
                {loading && <InlineLoading active />}
                {!loading && (
                  <Button
                    type="submit"
                    kind="secondary"
                    disabled={editingMode === EditingState.UPDATE && !dirty}
                    renderIcon={Save20}
                    hasIconOnly
                    tooltipPosition="left"
                    iconDescription={`Save changes to reserve requirement: ${reason}?`}
                  />
                )}
                {editingMode === EditingState.CREATE && <div style={{ width: "50px" }} />}
                {editingMode === EditingState.UPDATE && (
                  <Button
                    onClick={() =>
                      handleDeleteReserveRequirement(reason, number_of_months, values.array_index)
                    }
                    kind="danger--tertiary"
                    renderIcon={TrashCan20}
                    hasIconOnly
                    tooltipPosition="left"
                    iconDescription={`Delete reserve requirement: ${reason}?`}
                  />
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
      <div style={{ marginBottom: "10px" }} />
    </>
  );
};

export default ReserveRequirements;
